import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { Form, message, UploadFile } from 'antd';
import { Button, Checkbox, Drawer, Icon, Input, Switch, TextArea } from 'ui';
import useQueryApiClient from 'utils/useQueryApiClient';
import { FileUploader } from '../../../../ui/UploadImage/FileUploader';
import { StyledQualityMarkDrawer } from './style';
import { getMimeType } from 'utils/imageHelpers';
import { RcFile } from 'antd/es/upload';

interface QualityMarkCreateUpdateDrawerProps {
  id?: number;
  isDrawerVisible: boolean;
  onClose: Function;
}

const QualityMarkCreateUpdateDrawer = ({
  id,
  isDrawerVisible,
  onClose,
}: QualityMarkCreateUpdateDrawerProps) => {
  const intl = useIntl();
  const [form] = Form.useForm();

  const [isImageValid, setImageValidated] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  useQueryApiClient({
    request: {
      url: `api/v2/qmark/${id}`,
      disableOnMount: !id,
    },
    onSuccess: (response) => {
      const parse = {
        ...response,
        conflict: Object.values(response.conflict),
      };
      handleRawFile(response, response.logo, response.id);
      form.setFieldsValue(parse);
    },
  });

  const { appendData } = useQueryApiClient({
    request: {
      url: id ? `api/v2/qmark/${id}` : `api/v2/qmark`,
      method: 'POST',
      multipart: true,
    },
    onSuccess: () => onClose(),
    onError: () =>
      message.error(intl.formatMessage({ id: 'message.error_save' })),
  });

  const { data: checkboxOptions } = useQueryApiClient({
    request: {
      url: `api/v2/qmark/select`,
    },
  });

  const handleRawFile = (imageRawFile: any, imageUrl: string, id: string) => {
    const urlParts = imageUrl.split('/');
    const fileName = urlParts[urlParts.length - 1] || 'image.png';

    const mimeType = getMimeType(fileName);

    const file = new File([imageRawFile], fileName, { type: mimeType });

    const rcFile: RcFile = {
      ...file,
      lastModifiedDate: new Date(file.lastModified),
      uid: id,
    };

    const antdObject: UploadFile = {
      lastModified: rcFile.lastModified,
      lastModifiedDate: rcFile.lastModifiedDate,
      name: rcFile.name,
      status: 'done',
      url: imageUrl,
      uid: rcFile.uid,
      originFileObj: rcFile,
      type: rcFile.type,
    };

    setFileList((prevFileList) => [...prevFileList, antdObject]);
  };

  const onFinish = async (values: any) => {
    try {
      await form.validateFields();

      let bodyFormData = new FormData();

      Object.entries(values).forEach((entry: any) => {
        if (fileList.length < 1 || !fileList) {
          throw new Error(intl.formatMessage({ id: 'error.add_images' }));
        }
        if (entry[0] === 'images') {
          fileList.map((file: UploadFile) => {
            bodyFormData.append(
              `dynamic_files[${file.uid}]`,
              file.originFileObj!
            );
            return file['uid'];
          });
        }

        if (typeof entry[1] === 'object') {
          bodyFormData.append(entry[0], JSON.stringify(entry[1]));
          return;
        }
        if (entry[0] === 'product') {
          bodyFormData.append(entry[0], String(!!entry[1]));
        } else {
          bodyFormData.append(entry[0], entry[1]);
        }
      });
      bodyFormData.append(
        `logo_image`,
        JSON.stringify(fileList.map((n) => n.originFileObj?.uid))
      );
      appendData(bodyFormData);
      form.resetFields();
      setFileList([]);
    } catch (error: any) {
      message.error(error?.message);
    }
  };

  const innerOnClose = () => {
    form.resetFields();
    setFileList([]);
    onClose();
  };

  return (
    <StyledQualityMarkDrawer
      open={isDrawerVisible}
      onClose={() => innerOnClose()}
      title={
        id ? (
          <p>{intl.formatMessage({ id: 'general.edit' })}</p>
        ) : (
          <p>{intl.formatMessage({ id: 'general.add' })}</p>
        )
      }
      footer={
        <div className={'footer'}>
          <Button
            label={intl.formatMessage({ id: 'general.back' })}
            onClick={innerOnClose}
          />
          <Button
            label={intl.formatMessage({ id: 'general.submit' })}
            type="primary"
            htmlType="submit"
            onClick={() => form.submit()}
          />
        </div>
      }
      closeIcon={<Icon name={'global-close-icon'} />}
    >
      <Form form={form} onFinish={onFinish} layout="vertical">
        <Input
          name="name"
          label={intl.formatMessage({ id: 'qmark.name' })}
          validations={'required'}
        />
        <TextArea
          name="description"
          label={intl.formatMessage({ id: 'qmark.description' })}
          validations={'required'}
        />
        <Switch
          name="product"
          label={intl.formatMessage({ id: 'qmark.product' })}
        />
        <Checkbox
          name="conflict"
          formLabel={intl.formatMessage({ id: 'qmark.not_conflicted' })}
          options={checkboxOptions}
          breakNewLine
        />
        <FileUploader
          onValidate={setImageValidated}
          onFileListChange={setFileList}
          fileList={fileList}
          multiple={false}
          title={intl.formatMessage({ id: 'general.logo' })}
          isCrop={true}
        />
      </Form>
    </StyledQualityMarkDrawer>
  );
};

export default QualityMarkCreateUpdateDrawer;
